<template>
  <div class="notification-center">
    <el-popover
      placement="bottom-end"
      width="400"
      trigger="click"
      :visible.sync="popoverVisible"
      popper-class="notification-popover">
      
      <div class="notification-header">
        <span class="header-title">消息中心</span>
        <div class="header-actions">
          <el-tooltip content="设置">
            <i class="el-icon-setting" @click="showSettings = true"></i>
          </el-tooltip>
          <el-tooltip content="全部已读">
            <i class="el-icon-check" @click="markAllAsRead"></i>
          </el-tooltip>
        </div>
      </div>

      <el-tabs v-model="activeTab" class="notification-tabs">
        <el-tab-pane :label="getTabLabel('all')" name="all">
          <div class="notification-list" ref="allList">
            <el-empty v-if="allNotifications.length === 0" description="暂无消息" :image-size="60"></el-empty>
            <div 
              v-for="item in allNotifications" 
              :key="item.id" 
              class="notification-item"
              :class="{ unread: !item.read }"
              @click="handleItemClick(item)">
              <div class="item-icon" :class="item.type">
                <i :class="getTypeIcon(item.type)"></i>
              </div>
              <div class="item-content">
                <div class="item-title">
                  <span class="title-text">{{ item.title }}</span>
                  <span class="item-time">{{ formatTime(item.createTime) }}</span>
                </div>
                <div class="item-desc">{{ item.text || item.content || item.detail }}</div>
              </div>
              <div class="item-actions" @click.stop>
                <i class="el-icon-close" @click="handleRemove(item)"></i>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="getTabLabel('message')" name="message">
          <div class="notification-list">
            <el-empty v-if="messageList.length === 0" description="暂无消息" :image-size="60"></el-empty>
            <div 
              v-for="item in messageList" 
              :key="item.id" 
              class="notification-item"
              :class="{ unread: !item.read }"
              @click="handleItemClick(item, 'message')">
              <div class="item-icon message">
                <i class="el-icon-chat-dot-round"></i>
              </div>
              <div class="item-content">
                <div class="item-title">
                  <span class="title-text">{{ item.title }}</span>
                  <span class="item-time">{{ formatTime(item.createTime) }}</span>
                </div>
                <div class="item-desc">{{ item.text || item.content }}</div>
              </div>
              <div class="item-actions" @click.stop>
                <i class="el-icon-close" @click="handleRemove(item, 'message')"></i>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="getTabLabel('notice')" name="notice">
          <div class="notification-list">
            <el-empty v-if="noticeList.length === 0" description="暂无通知" :image-size="60"></el-empty>
            <div 
              v-for="item in noticeList" 
              :key="item.id" 
              class="notification-item"
              :class="{ unread: !item.read }"
              @click="handleItemClick(item, 'notice')">
              <div class="item-icon notice">
                <i class="el-icon-bell"></i>
              </div>
              <div class="item-content">
                <div class="item-title">
                  <span class="title-text">{{ item.title }}</span>
                  <span class="item-time">{{ formatTime(item.createTime) }}</span>
                </div>
                <div class="item-desc">{{ item.text || item.content }}</div>
              </div>
              <div class="item-actions" @click.stop>
                <i class="el-icon-close" @click="handleRemove(item, 'notice')"></i>
              </div>
            </div>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="getTabLabel('alert')" name="alert">
          <div class="notification-list">
            <el-empty v-if="alertList.length === 0" description="暂无告警" :image-size="60"></el-empty>
            <div 
              v-for="item in alertList" 
              :key="item.id" 
              class="notification-item"
              :class="{ unread: !item.read }"
              @click="handleItemClick(item, 'alert')">
              <div class="item-icon alert">
                <i class="el-icon-warning"></i>
              </div>
              <div class="item-content">
                <div class="item-title">
                  <span class="title-text">{{ item.title }}</span>
                  <span class="item-time">{{ formatTime(item.createTime) }}</span>
                </div>
                <div class="item-desc">{{ item.text || item.content }}</div>
              </div>
              <div class="item-actions" @click.stop>
                <i class="el-icon-close" @click="handleRemove(item, 'alert')"></i>
              </div>
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>

      <div class="notification-footer">
        <el-button type="text" size="small" @click="clearCurrentType">清空当前</el-button>
        <el-button type="text" size="small" @click="goToMessageCenter">查看全部</el-button>
      </div>

      <template slot="reference">
        <el-badge 
          :value="unreadTotal" 
          :hidden="unreadTotal === 0" 
          type="danger" 
          :max="99" 
          class="notification-badge">
          <el-button circle :type="isConnected ? 'primary' : 'info'" icon="el-icon-bell" size="mini"/>
        </el-badge>
      </template>
    </el-popover>

    <el-dialog 
      title="消息详情" 
      :visible.sync="detailDialogVisible" 
      width="500px"
      class="notification-detail-dialog">
      <div class="detail-content">
        <div class="detail-header">
          <span class="detail-type" :class="currentDetail?.type">
            {{ getTypeName(currentDetail?.type) }}
          </span>
          <span class="detail-time">{{ formatTime(currentDetail?.createTime) }}</span>
        </div>
        <h3 class="detail-title">{{ currentDetail?.title }}</h3>
        <div class="detail-body" v-html="currentDetail?.detail || currentDetail?.text || currentDetail?.content"></div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button v-if="currentDetail?.backPath" type="primary" size="small" @click="handleBack">填写回执</el-button>
        <el-button v-if="currentDetail?.toPath" type="success" size="small" @click="handleToPath">前往处理</el-button>
        <el-button size="small" @click="detailDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <el-dialog 
      title="通知设置" 
      :visible.sync="showSettings" 
      width="400px"
      class="notification-settings-dialog">
      <div class="settings-content">
        <el-form label-position="right" label-width="120px">
          <el-form-item label="声音提醒">
            <el-switch v-model="settings.soundEnabled"></el-switch>
          </el-form-item>
          <el-form-item label="桌面通知">
            <el-switch v-model="settings.desktopNotificationEnabled"></el-switch>
          </el-form-item>
        </el-form>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button size="small" @click="saveSettings">保存</el-button>
        <el-button size="small" @click="showSettings = false">取消</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
/**
 * 消息通知中心组件
 * @author PeterLee
 * @since 2026-03-15
 */
import { mapState, mapGetters, mapActions } from 'vuex'
import { getWebSocketService } from '@/utils/websocketService'

export default {
  name: 'NotificationCenter',

  data() {
    return {
      popoverVisible: false,
      activeTab: 'all',
      detailDialogVisible: false,
      showSettings: false,
      currentDetail: null,
      wsService: null,
      settings: {
        soundEnabled: true,
        desktopNotificationEnabled: true
      }
    }
  },

  computed: {
    ...mapState('notification', ['isConnected', 'messages', 'notices', 'alerts', 'settings']),
    ...mapGetters('notification', ['allNotifications', 'unreadTotal', 'getTypeList', 'getUnreadByType']),
    
    messageList() {
      return this.getTypeList('message')
    },
    
    noticeList() {
      return this.getTypeList('notice')
    },
    
    alertList() {
      return this.getTypeList('alert')
    }
  },

  mounted() {
    // WebSocket 已在登录时初始化，这里只需要初始化 store
    this.$store.dispatch('notification/init')
  },

  beforeDestroy() {
    // 不再需要断开 WebSocket，因为它由全局管理
  },

  methods: {
    ...mapActions('notification', ['markRead', 'markAllRead', 'clearType', 'clearAll', 'remove', 'updateSettings']),

    initWebSocket() {
      const token = this.$store.state.user.UserToken
      if (token) {
        this.wsService = getWebSocketService(this.$store)
        this.wsService.connect(token)
      }
    },

    togglePopover() {
      this.popoverVisible = !this.popoverVisible
    },

    getTabLabel(type) {
      const labels = {
        all: '全部',
        message: '消息',
        notice: '通知',
        alert: '告警'
      }
      let label = labels[type] || type
      if (type !== 'all') {
        const count = this.getUnreadByType(type)
        if (count > 0) {
          label += ` (${count})`
        }
      }
      return label
    },

    getTypeIcon(type) {
      const icons = {
        message: 'el-icon-chat-dot-round',
        notice: 'el-icon-bell',
        alert: 'el-icon-warning'
      }
      return icons[type] || 'el-icon-info'
    },

    getTypeName(type) {
      const names = {
        message: '消息',
        notice: '通知',
        alert: '告警'
      }
      return names[type] || '通知'
    },

    formatTime(timeStr) {
      if (!timeStr) return ''
      const time = new Date(timeStr)
      const now = new Date()
      const diff = now - time
      
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`
      
      return time.toLocaleDateString()
    },

    handleItemClick(item, type) {
      const msgType = type || item.type
      if (!item.read) {
        this.markRead({ type: msgType, id: item.id })
      }
      this.currentDetail = item
      this.detailDialogVisible = true
      this.popoverVisible = false
    },

    handleRemove(item, type) {
      const msgType = type || item.type
      this.remove({ type: msgType, id: item.id })
    },

    markAllAsRead() {
      if (this.activeTab === 'all') {
        this.markAllRead()
      } else {
        this.markAllRead(this.activeTab)
      }
    },

    clearCurrentType() {
      if (this.activeTab === 'all') {
        this.$confirm('确定要清空所有消息吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.clearAll()
        }).catch(() => {})
      } else {
        this.clearType(this.activeTab)
      }
    },

    goToMessageCenter() {
      this.popoverVisible = false
      this.$router.push('/notification/notification')
    },

    handleBack() {
      if (this.currentDetail?.backPath) {
        this.$router.push(this.currentDetail.backPath)
        this.detailDialogVisible = false
      }
    },

    handleToPath() {
      if (this.currentDetail?.toPath) {
        this.$router.push(this.currentDetail.toPath)
        this.detailDialogVisible = false
      }
    },

    saveSettings() {
      this.updateSettings(this.settings)
      this.showSettings = false
      this.$message.success('设置已保存')
    }
  }
}
</script>

<style lang="less" scoped>
.notification-center {
  display: inline-block;
}

.notification-badge {
  margin-right: 10px;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 0 15px 0;
  border-bottom: 1px solid #ebeef5;
  
  .header-title {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }
  
  .header-actions {
    i {
      font-size: 18px;
      color: #909399;
      cursor: pointer;
      margin-left: 15px;
      transition: color 0.2s;
      
      &:hover {
        color: #409eff;
      }
    }
  }
}

.notification-tabs {
  margin-top: 15px;
  
  ::v-deep .el-tabs__header {
    margin-bottom: 10px;
  }
  
  ::v-deep .el-tabs__nav-wrap::after {
    display: none;
  }
}

.notification-list {
  max-height: 350px;
  overflow-y: auto;
  padding-right: 5px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: #dcdfe6;
    border-radius: 2px;
  }
}

.notification-item {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 8px;
  background-color: #f5f7fa;
  
  &:hover {
    background-color: #ecf5ff;
  }
  
  &.unread {
    background-color: #f0f9ff;
    
    .item-content {
      .item-title {
        .title-text {
          font-weight: 600;
        }
      }
    }
  }
  
  .item-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-right: 12px;
    
    i {
      font-size: 20px;
      color: #fff;
    }
    
    &.message {
      background: linear-gradient(135deg, #67c23a, #85ce61);
    }
    
    &.notice {
      background: linear-gradient(135deg, #e6a23c, #ebb563);
    }
    
    &.alert {
      background: linear-gradient(135deg, #f56c6c, #f78989);
    }
  }
  
  .item-content {
    flex: 1;
    min-width: 0;
    
    .item-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 4px;
      
      .title-text {
        font-size: 14px;
        color: #303133;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      
      .item-time {
        font-size: 12px;
        color: #c0c4cc;
        flex-shrink: 0;
        margin-left: 8px;
      }
    }
    
    .item-desc {
      font-size: 13px;
      color: #606266;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      line-height: 1.4;
    }
  }
  
  .item-actions {
    flex-shrink: 0;
    margin-left: 8px;
    
    i {
      font-size: 16px;
      color: #c0c4cc;
      cursor: pointer;
      padding: 4px;
      transition: color 0.2s;
      
      &:hover {
        color: #f56c6c;
      }
    }
  }
}

.notification-footer {
  display: flex;
  justify-content: space-between;
  padding-top: 15px;
  border-top: 1px solid #ebeef5;
  margin-top: 15px;
}

.detail-content {
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    
    .detail-type {
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 12px;
      color: #fff;
      
      &.message {
        background-color: #67c23a;
      }
      
      &.notice {
        background-color: #e6a23c;
      }
      
      &.alert {
        background-color: #f56c6c;
      }
    }
    
    .detail-time {
      font-size: 13px;
      color: #909399;
    }
  }
  
  .detail-title {
    font-size: 18px;
    color: #303133;
    margin-bottom: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #ebeef5;
  }
  
  .detail-body {
    font-size: 14px;
    color: #606266;
    line-height: 1.8;
    max-height: 300px;
    overflow-y: auto;
  }
}

.settings-content {
  padding: 10px 0;
}
</style>

<style>
.notification-popover {
  padding: 20px !important;
}
</style>
